/**
 * Thread actions and screen state (2026-10-29). Opening a thread is a Hub
 * view of the thread's ID: Hub shows its parent channel with the thread in
 * the panel beside it (full screen on a phone), so Back, Jump and search
 * results reach a thread like any channel.
 */
import {
  ChannelType,
  isThreadChannelType,
  Permissions,
  type Channel,
  type Message,
  type ModifyThreadRequest,
  type Snowflake,
} from '@neosocial/protocol';
import { create } from 'zustand';
import { tr } from '../i18n';
import { failToast } from '../mod/actions';
import { updateData, useData } from '../store/data';
import { ackChannel, sendMessage } from '../store/messages';
import type { DataState } from '../store/reducer';
import { can, channelPermissions } from '../store/selectors';
import { goHub, useUi } from '../store/ui';
import { confirmDialog, promptDialog, toast } from '../ui/overlay';
import { threadsApi } from './api';
import { putThread, putThreads } from './store';

/** The threads list or the create form open beside a channel (not a thread: that is the Hub view). */
export interface ThreadPanelState {
  channelId: Snowflake;
  view: 'list' | 'create';
  /** The create form starts the thread from this message. */
  fromMessage?: Snowflake;
}

export const useThreadUi = create<{ panel: ThreadPanelState | null }>(() => ({ panel: null }));

export function openThreadList(channelId: Snowflake): void {
  leaveOpenThread(channelId);
  useThreadUi.setState({ panel: { channelId, view: 'list' } });
}

export function openThreadCreate(channelId: Snowflake, fromMessage?: Snowflake): void {
  leaveOpenThread(channelId);
  useThreadUi.setState({ panel: { channelId, view: 'create', fromMessage } });
}

export function closeThreadPanel(): void {
  useThreadUi.setState({ panel: null });
}

/** A thread open over this channel goes back to the channel. */
function leaveOpenThread(parentId: Snowflake): void {
  const v = useUi.getState().hub;
  if (v?.kind !== 'channel') return;
  const c = useData.getState().channels[v.channelId];
  if (c && isThreadChannelType(c.type) && c.parent_id === parentId)
    goHub({ kind: 'channel', channelId: parentId });
}

/** Opens a thread beside its channel (its own pane on a phone). */
export function openThread(thread: Channel): void {
  if (!useData.getState().channels[thread.id]) updateData((s) => putThread(s, thread));
  closeThreadPanel();
  goHub({ kind: 'channel', channelId: thread.id });
}

/** Closes the open thread: its channel stays. */
export function closeThread(thread: Pick<Channel, 'parent_id'>): void {
  if (thread.parent_id) goHub({ kind: 'channel', channelId: thread.parent_id });
}

/** The thread a Hub view shows (its channel is the parent), or null. */
export function threadOfView(s: Pick<DataState, 'channels'>, channelId: Snowflake): Channel | null {
  const c = s.channels[channelId];
  return c && isThreadChannelType(c.type) && c.parent_id && s.channels[c.parent_id] ? c : null;
}

/* ------------------------------- creating ------------------------------- */

export interface CreateInput {
  channelId: Snowflake;
  name: string;
  isPrivate: boolean;
  autoArchive?: 60 | 1440 | 4320 | 10080;
  fromMessage?: Snowflake;
  /** The first message (optional with a starter message). */
  first?: string;
}

/** Starts a thread, sends its first message, opens it. Null when refused (toasted). */
export async function createThread(o: CreateInput): Promise<Channel | null> {
  try {
    const thread = o.fromMessage
      ? await threadsApi.startFromMessage(o.channelId, o.fromMessage, {
          name: o.name,
          auto_archive_duration: o.autoArchive,
        })
      : await threadsApi.start(o.channelId, {
          name: o.name,
          type: o.isPrivate ? ChannelType.PRIVATE_THREAD : ChannelType.PUBLIC_THREAD,
          auto_archive_duration: o.autoArchive,
        });
    updateData((s) => putThread(s, thread));
    openThread(thread);
    const first = o.first?.trim();
    if (first) sendMessage(thread.id, first);
    return thread;
  } catch (e) {
    failToast(e);
    return null;
  }
}

/* ------------------------------- changing ------------------------------- */

async function modify(t: Channel, b: ModifyThreadRequest, done?: string): Promise<boolean> {
  try {
    const out = await threadsApi.modify(t.id, b);
    updateData((s) => putThread(s, out));
    if (done) toast(tr().t(done), { icon: 'check' });
    return true;
  } catch (e) {
    failToast(e);
    return false;
  }
}

export const setArchived = (t: Channel, archived: boolean) =>
  modify(t, { archived }, archived ? 'th.menu.archived' : 'th.menu.unarchived');
export const setLocked = (t: Channel, locked: boolean) =>
  modify(t, { locked }, locked ? 'th.menu.locked' : 'th.menu.unlocked');
export const setInvitable = (t: Channel, invitable: boolean) => modify(t, { invitable });
export const setAutoArchive = (t: Channel, minutes: 60 | 1440 | 4320 | 10080) =>
  modify(t, { auto_archive_duration: minutes });

export async function renameThread(t: Channel): Promise<void> {
  const { t: tt } = tr();
  const name = await promptDialog({
    title: tt('th.menu.rename'),
    body: tt('th.menu.renameBody'),
    inputLabel: tt('th.create.name'),
    confirmLabel: tt('c.save'),
    icon: 'edit',
  });
  const v = name?.trim();
  if (!v || v === t.name) return;
  await modify(t, { name: [...v].slice(0, 100).join('') }, 'th.menu.renamed');
}

export async function deleteThread(t: Channel): Promise<void> {
  const { t: tt } = tr();
  const ok = await confirmDialog({
    title: tt('th.menu.deleteTitle'),
    body: tt('th.menu.deleteBody'),
    confirmLabel: tt('th.menu.delete'),
    danger: true,
    icon: 'trash',
  });
  if (!ok) return;
  try {
    await threadsApi.delete(t.id);
    closeThread(t);
    toast(tt('th.menu.deleted'), { icon: 'trash' });
  } catch (e) {
    failToast(e);
  }
}

export async function joinThread(t: Channel): Promise<void> {
  try {
    await threadsApi.join(t.id);
    toast(tr().t('th.panel.joined'), { icon: 'check' });
  } catch (e) {
    failToast(e);
  }
}

export async function leaveThread(t: Channel): Promise<void> {
  try {
    await threadsApi.leave(t.id);
    toast(tr().t('th.panel.left'), { icon: 'arrow-out' });
  } catch (e) {
    failToast(e);
  }
}

export function markThreadRead(t: Channel): void {
  ackChannel(t.id);
}

/** Loads a channel's threads into the store (the list fetches archived ones itself). */
export async function loadActive(guildId: Snowflake): Promise<void> {
  const r = await threadsApi.active(guildId);
  updateData((s) => putThreads(s, r.threads, r.members));
}

/* ------------------------------- permissions ------------------------------ */

/** What the signed-in user may do with threads in a channel or a thread (as the server checks). */
export function threadPerms(s: DataState, ch: Channel | undefined) {
  const p = channelPermissions(s, ch);
  return {
    view: can(p, Permissions.VIEW_CHANNEL),
    createPublic: can(p, Permissions.CREATE_PUBLIC_THREADS),
    createPrivate: can(p, Permissions.CREATE_PRIVATE_THREADS),
    history: can(p, Permissions.READ_MESSAGE_HISTORY),
    manage: can(p, Permissions.MANAGE_THREADS),
    /** In a thread: may write in it (Send Messages in Threads, not locked). */
    send: can(p, Permissions.SEND_MESSAGES),
  };
}

/** A message can start a thread: an ordinary message in a text channel, without one yet. */
export function canStartFrom(s: DataState, m: Message, ch: Channel | undefined): boolean {
  if (!ch || ch.type !== ChannelType.GUILD_TEXT) return false;
  if (m.type !== 0 && m.type !== 19) return false;
  if (m.thread || s.channels[m.id]) return false;
  return threadPerms(s, ch).createPublic;
}
